import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Dialog, DialogHeader, DialogBody, DialogFooter,
    Button, Typography, Card, CardBody, Chip, Alert
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHistory, faUndo, faChevronDown, faChevronUp,
    faCheckCircle, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
const { rootUrl } = require('../../../config/config');

const VersionHistoryModal = ({ remittanceId, open, onClose, onRestore, token, formatCurrency, username = 'system' }) => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedVersions, setExpandedVersions] = useState({});
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (open && remittanceId) {
            fetchVersionHistory();
        }
    }, [open, remittanceId]);

    const fetchVersionHistory = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${rootUrl}/api/remittance/${remittanceId}/versions`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                setVersions(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching version history:', error);
            setError('Error loading version history: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = async (versionId, versionNumber) => {
        if (!window.confirm(`Restore version ${versionNumber}? This will create a new version with these values.`)) {
            return;
        }

        try {
            const response = await axios.post(
                `${rootUrl}/api/remittance/${remittanceId}/restore/${versionId}`,
                { restored_by: username },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setSuccess('Version restored successfully');
                setTimeout(() => setSuccess(null), 3000);
                onRestore();
                onClose();
            }
        } catch (error) {
            console.error('Error restoring version:', error);
            setError('Error restoring version: ' + (error.response?.data?.message || error.message));
            setTimeout(() => setError(null), 5000);
        }
    };

    const toggleExpand = (versionId) => {
        setExpandedVersions(prev => ({
            ...prev,
            [versionId]: !prev[versionId]
        }));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PENDING': return 'gray';
            case 'PROCESSED': return 'blue';
            case 'APPROVED': return 'green';
            case 'PAID': return 'purple';
            default: return 'gray';
        }
    };

    return (
        <Dialog open={open} handler={onClose} size="xl">
            <DialogHeader className="flex items-center gap-2">
                <FontAwesomeIcon icon={faHistory} />
                Version History
            </DialogHeader>
            <DialogBody className="max-h-[70vh] overflow-y-auto">
                {error && (
                    <Alert
                        color="red"
                        icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                        className="mb-4"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert
                        color="green"
                        icon={<FontAwesomeIcon icon={faCheckCircle} />}
                        className="mb-4"
                        onClose={() => setSuccess(null)}
                    >
                        {success}
                    </Alert>
                )}
                {loading ? (
                    <div className="text-center py-8">
                        <Typography>Loading version history...</Typography>
                    </div>
                ) : versions.length === 0 ? (
                    <div className="text-center py-8">
                        <Typography color="gray">No version history available</Typography>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {versions.map((version) => (
                            <Card key={version.id} className="border border-gray-200">
                                <CardBody>
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <div className="flex items-center gap-3 mb-2">
                                                <Typography variant="h6">
                                                    Version {version.version}
                                                </Typography>
                                                <Chip
                                                    value={version.status}
                                                    color={getStatusColor(version.status)}
                                                    size="sm"
                                                />
                                                {version.is_locked && (
                                                    <Chip value="Locked" color="amber" size="sm" />
                                                )}
                                            </div>
                                            <Typography variant="small" color="gray">
                                                {new Date(version.version_date).toLocaleString()}
                                            </Typography>
                                            <Typography variant="small" color="gray">
                                                By: {version.version_by}
                                            </Typography>
                                            {version.version_reason && (
                                                <Typography variant="small" className="mt-1">
                                                    Reason: {version.version_reason}
                                                </Typography>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="outlined"
                                                onClick={() => toggleExpand(version.id)}
                                                className="flex items-center gap-2"
                                            >
                                                <FontAwesomeIcon
                                                    icon={expandedVersions[version.id] ? faChevronUp : faChevronDown}
                                                />
                                                {expandedVersions[version.id] ? 'Hide' : 'Show'} Details
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handleRestore(version.id, version.version)}
                                                className="bg-blue-500 flex items-center gap-2"
                                            >
                                                <FontAwesomeIcon icon={faUndo} />
                                                Restore
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Summary Row */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
                                        <div>
                                            <Typography variant="small" color="gray">Working Days</Typography>
                                            <Typography variant="small" className="font-bold">
                                                {version.working_days} days
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography variant="small" color="gray">Week Target</Typography>
                                            <Typography variant="small" className="font-bold">
                                                {formatCurrency(version.week_target)}
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography variant="small" color="gray">Net Income</Typography>
                                            <Typography variant="small" className="font-bold text-green-600">
                                                {formatCurrency(version.net_income)}
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography variant="small" color="gray">Balance After Payout</Typography>
                                            <Typography variant="small" className="font-bold text-purple-600">
                                                {formatCurrency(version.closing_balance_after_payout)}
                                            </Typography>
                                        </div>
                                    </div>

                                    {/* Expanded Details */}
                                    {expandedVersions[version.id] && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                <div>
                                                    <Typography variant="small" color="gray">Vehicle</Typography>
                                                    <Typography variant="small" className="font-bold">
                                                        {version.plate_no}
                                                    </Typography>
                                                    <Typography variant="small" color="gray">
                                                        {version.make} {version.model}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">Owner</Typography>
                                                    <Typography variant="small" className="font-bold">
                                                        {version.owner_name} ({version.owner_code})
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">Week</Typography>
                                                    <Typography variant="small" className="font-bold">
                                                        {version.week_key}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">Previous Balance</Typography>
                                                    <Typography variant="small">
                                                        {formatCurrency(version.prev_balance)}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">WKI Total</Typography>
                                                    <Typography variant="small" className="text-green-600">
                                                        {formatCurrency(version.wki_total)}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">WKR Total</Typography>
                                                    <Typography variant="small" className="text-blue-600">
                                                        {formatCurrency(version.wkr_total)}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">Losses Total</Typography>
                                                    <Typography variant="small" className="text-red-600">
                                                        {formatCurrency(version.losses_total)}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">Adjustments</Typography>
                                                    <Typography variant="small">
                                                        {formatCurrency(version.adjustments_total)}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">Expenses</Typography>
                                                    <Typography variant="small">
                                                        {formatCurrency(version.expenses_total)}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">Total Charges</Typography>
                                                    <Typography variant="small">
                                                        {formatCurrency(version.total_charges)}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">Balance Before Payout</Typography>
                                                    <Typography variant="small">
                                                        {formatCurrency(version.closing_balance_before_payout)}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" color="gray">Owner Payout</Typography>
                                                    <Typography variant="small" className="font-bold">
                                                        {formatCurrency(version.owner_payout)}
                                                    </Typography>
                                                </div>
                                                {version.payment_date && (
                                                    <>
                                                        <div>
                                                            <Typography variant="small" color="gray">Payment Date</Typography>
                                                            <Typography variant="small">
                                                                {new Date(version.payment_date).toLocaleDateString()}
                                                            </Typography>
                                                        </div>
                                                        <div>
                                                            <Typography variant="small" color="gray">Payment Reference</Typography>
                                                            <Typography variant="small">
                                                                {version.payment_reference}
                                                            </Typography>
                                                        </div>
                                                    </>
                                                )}
                                                {version.notes && (
                                                    <div className="col-span-2 md:col-span-3">
                                                        <Typography variant="small" color="gray">Notes</Typography>
                                                        <Typography variant="small" className="whitespace-pre-wrap">
                                                            {version.notes}
                                                        </Typography>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        ))}
                    </div>
                )}
            </DialogBody>
            <DialogFooter>
                <Button variant="text" onClick={onClose}>
                    Close
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default VersionHistoryModal;
